import {
  IAssignResult,
  IEvaluationPreSignedQuestionAnsweredUrlRequest,
  ILCHttpResponse,
  IPaginationResponse,
  IPreSignedIndexedURL,
  IRiskHREvaluationResults,
  IUser,
} from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import {
  IAssignEvaluationRequest,
  IEvaluationComparisonRequest,
  IEvaluationComparisonResponse,
  IEvaluationSpecific,
  IGetEvaluationRequest,
  IGetSpecificEvaluationRequest,
  IResendEmailRequest,
  IResendEmailResult,
  IUnAssignEvaluationRequest,
} from '../../interfaces/evaluations.interface'
import { IEvaluationSummary } from '../pages/riskhr/riskHrEvaluations/columns/riskHrEvaluations.columns'
import { RootState } from '../redux/store'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'
interface EvaluationApiResponse {
  evaluation: {
    data: IEvaluationSpecific
    message: string
    statusCode: number
    timestamp: string
  }
}

export const evaluationsApi = createApi({
  reducerPath: 'evaluationsApi',
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/evaluations`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  endpoints: (builder) => ({
    assignEvaluation: builder.mutation<IAssignResult, IAssignEvaluationRequest>({
      query: (assignRequest: IAssignEvaluationRequest) => ({
        url: `/assign`,
        method: 'POST',
        body: assignRequest,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IAssignResult>, meta) => {
        return rawResult.data as IAssignResult
      },
      // No cache invalidation needed since we're handling license updates client-side
    }),
    unassignEvaluation: builder.mutation<string[], IUnAssignEvaluationRequest[]>({
      query: (unassignRequest: IUnAssignEvaluationRequest[]) => ({
        url: `/unassign`,
        method: 'POST',
        body: unassignRequest,
      }),
      transformResponse: (rawResult: ILCHttpResponse<string[]>, meta) => {
        return rawResult.data as string[]
      },
      // No cache invalidation needed since we're handling license updates client-side
    }),
    resendEvaluationEmail: builder.mutation<IResendEmailResult, IResendEmailRequest[]>({
      query: (resendAssignEmailRequest: IResendEmailRequest[]) => ({
        url: `/assign/resend`,
        method: 'POST',
        body: resendAssignEmailRequest,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IResendEmailResult>, meta) => {
        return rawResult.data as IResendEmailResult
      },
    }),
    sendAssessmentReminders: builder.mutation<IResendEmailResult, IResendEmailRequest[]>({
      query: (reminderRequest: IResendEmailRequest[]) => ({
        url: `/reminders`,
        method: 'POST',
        body: reminderRequest,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IResendEmailResult>) => {
        return rawResult.data as IResendEmailResult
      },
    }),
    // Gateway endpoint: POST /evaluations/retry -> EvaluationsController.retryEvaluation
    // (guarded by ePermission.ASSESSMENT_RETRY_PROCESSING, resolved against baseUrl `${GATEWAY_URL}/evaluations`)
    retryEvaluation: builder.mutation<boolean, { identity: string; evaluationId: string }>({
      query: (body) => ({
        url: `/retry`,
        method: 'POST',
        body,
      }),
      transformResponse: (rawResult: ILCHttpResponse<boolean>) => {
        return !!rawResult.data
      },
    }),
    archiveEvaluations: builder.mutation<IRiskHREvaluationResults[], { evaluationIds: string[] }>({
      query: (archiveEvaluationsDto) => ({
        url: `/archive`,
        method: 'PUT',
        body: {
          evaluationIds: archiveEvaluationsDto.evaluationIds,
        },
      }),
      transformResponse: (rawResult: ILCHttpResponse<IRiskHREvaluationResults[]>, meta) => {
        return rawResult.data as IRiskHREvaluationResults[]
      },
    }),
    restoreEvaluations: builder.mutation<IRiskHREvaluationResults[], { evaluationIds: string[] }>({
      query: (restoreEvaluationsDto) => ({
        url: `/restore`,
        method: 'PUT',
        body: {
          evaluationIds: restoreEvaluationsDto.evaluationIds,
        },
      }),
      transformResponse: (rawResult: ILCHttpResponse<IRiskHREvaluationResults[]>, meta) => {
        return rawResult.data as IRiskHREvaluationResults[]
      },
    }),
    deleteArchivedEvaluations: builder.mutation<IRiskHREvaluationResults[], { evaluationIds: string[] }>({
      query: (deleteEvaluationsDto) => ({
        url: `/archive/delete`,
        method: 'DELETE',
        body: {
          evaluationIds: deleteEvaluationsDto.evaluationIds,
        },
      }),
      transformResponse: (rawResult: ILCHttpResponse<IRiskHREvaluationResults[]>, meta) => {
        return rawResult.data as IRiskHREvaluationResults[]
      },
    }),
    getEvaluationsResults: builder.mutation<IPaginationResponse<IEvaluationSummary[]>, IGetEvaluationRequest>({
      query: ({ pageIndex, pageSize, sortBy, search }) => {
        return {
          url: 'get-pagination',
          method: 'POST',
          body: {
            pageIndex,
            pageSize,
            sortBy,
            search,
          },
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<IPaginationResponse<IEvaluationSummary[]>>, meta) => {
        return rawResult.data as IPaginationResponse<IEvaluationSummary[]>
      },
    }),
    getArchivedEvaluationsResults: builder.mutation<IPaginationResponse<any[]>, IGetEvaluationRequest>({
      query: ({ pageIndex, pageSize, sortBy, search }) => {
        return {
          url: 'archived/get-pagination',
          method: 'POST',
          body: {
            pageIndex,
            pageSize,
            sortBy,
            search,
          },
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<IPaginationResponse<any[]>>, meta) => {
        return rawResult.data as IPaginationResponse<any[]>
      },
    }),
    getEvaluationsComparison: builder.query<IEvaluationComparisonResponse[], IEvaluationComparisonRequest>({
      query: ({ identity, language }) => {
        // Build URL with optional language parameter
        const baseUrl = `comparison/${identity}`
        const url = language ? `${baseUrl}/${language}` : baseUrl

        return {
          url,
          method: 'GET',
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<IEvaluationComparisonResponse[]>, meta) => {
        return rawResult.data as IEvaluationComparisonResponse[]
      },
    }),
    getSpecificEvaluationsResults: builder.mutation<IEvaluationSpecific, IGetSpecificEvaluationRequest>({
      query: ({ identity, evaluationId, language }) => {
        // Build URL with optional language parameter
        const baseUrl = `results/${identity}/${evaluationId}`
        const url = language ? `${baseUrl}/${language}` : baseUrl

        return {
          url,
          method: 'GET',
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<IEvaluationSpecific>, meta) => {
        return rawResult.data as IEvaluationSpecific
      },
    }),
    getPreSignedEvaluationQuestionSoundFile: builder.mutation<IPreSignedIndexedURL[], IEvaluationPreSignedQuestionAnsweredUrlRequest>({
      query: (body) => {
        return {
          url: '/pre-signed/evaluation-question',
          method: 'POST',
          body,
        }
      },
      transformResponse: (rawResult: ILCHttpResponse<IPreSignedIndexedURL[]>, meta) => {
        return rawResult.data as IPreSignedIndexedURL[]
      },
    }),
    getUsersForAssignment: builder.query<IUser[], string[]>({
      query: (identities) => ({
        url: `/users-for-assignment`,
        method: 'POST',
        body: { identities },
      }),
      transformResponse: (rawResult: ILCHttpResponse<IUser[]>, meta) => {
        return rawResult.data as IUser[]
      },
    }),
  }),
})

export const {
  useArchiveEvaluationsMutation,
  useRestoreEvaluationsMutation,
  useDeleteArchivedEvaluationsMutation,
  useAssignEvaluationMutation,
  useGetEvaluationsResultsMutation,
  useGetArchivedEvaluationsResultsMutation,
  useGetSpecificEvaluationsResultsMutation,
  useGetPreSignedEvaluationQuestionSoundFileMutation,
  useResendEvaluationEmailMutation,
  useSendAssessmentRemindersMutation,
  useRetryEvaluationMutation,
  useUnassignEvaluationMutation,
  useLazyGetEvaluationsComparisonQuery,
  useGetUsersForAssignmentQuery,
} = evaluationsApi
